import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type ChatUser = Record<string, unknown>;

/** Response from fetchCurrentlyViewingAssetApi: analytics + asset details when assetId present */
export type CurrentlyViewingAsset = Record<string, unknown> | null;

interface EngageContextValue {
  selectedChatUser: ChatUser | null;
  setSelectedChatUser: (user: ChatUser | null) => void;
  currentlyViewingAsset: CurrentlyViewingAsset;
  setCurrentlyViewingAsset: (asset: CurrentlyViewingAsset) => void;
}

const EngageContext = createContext<EngageContextValue | null>(null);

export function EngageProvider({ children }: { children: ReactNode }) {
  const [selectedChatUser, setSelectedChatUserState] = useState<ChatUser | null>(null);
  const [currentlyViewingAsset, setCurrentlyViewingAssetState] = useState<CurrentlyViewingAsset>(null);

  const setSelectedChatUser = useCallback((user: ChatUser | null) => {
    setSelectedChatUserState(user);
    if (user == null) {
      setCurrentlyViewingAssetState(null);
    }
  }, []);

  const setCurrentlyViewingAsset = useCallback((asset: CurrentlyViewingAsset) => {
    setCurrentlyViewingAssetState(asset);
  }, []);

  const value: EngageContextValue = {
    selectedChatUser,
    setSelectedChatUser,
    currentlyViewingAsset,
    setCurrentlyViewingAsset,
  };

  return (
    <EngageContext.Provider value={value}>
      {children}
    </EngageContext.Provider>
  );
}

export function useEngageContext(): EngageContextValue {
  const context = useContext(EngageContext);
  if (context == null) {
    throw new Error("useEngageContext must be used within EngageProvider");
  }
  return context;
}
